import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

/**
 * Hook para obtener y aplicar campos obligatorios configurados
 * @param {string} modulo - ID del módulo (ej: "gestion_humana", "visitantes")
 * @param {string} formulario - ID del formulario (ej: "empleados", "registro_entrada")
 * @param {function} getAuthHeaders - Función para obtener headers de autenticación
 */
export function useCamposObligatorios(modulo, formulario, getAuthHeaders) {
  const [camposConfig, setCamposConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchConfig = useCallback(async () => {
    if (!modulo || !formulario) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(
        `${API}/campos-obligatorios/${modulo}/${formulario}`,
        getAuthHeaders()
      );
      
      // Convert array to object for easy lookup
      const configMap = {};
      (res.data.campos || []).forEach(campo => {
        // Support both 'id' and 'campo_id' field names
        const fieldId = campo.id || campo.campo_id;
        configMap[fieldId] = campo.obligatorio;
      });
      
      setCamposConfig(configMap);
      setError(null);
    } catch (err) {
      console.error('Error loading campos obligatorios:', err);
      setError(err);
      // Use empty config on error - fields will use their defaults
      setCamposConfig({});
    } finally {
      setLoading(false);
    }
  }, [modulo, formulario, getAuthHeaders]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  /**
   * Check if a field is required
   * @param {string} fieldId - ID of the field to check
   * @param {boolean} defaultRequired - Default value if not configured
   * @returns {boolean}
   */
  const isRequired = useCallback((fieldId, defaultRequired = false) => {
    if (camposConfig.hasOwnProperty(fieldId)) {
      return camposConfig[fieldId];
    }
    return defaultRequired;
  }, [camposConfig]);

  /**
   * Get props for an input field including required attribute
   * @param {string} fieldId - ID of the field
   * @param {boolean} defaultRequired - Default required value
   * @returns {object} Props object with required attribute
   */
  const getFieldProps = useCallback((fieldId, defaultRequired = false) => {
    const required = isRequired(fieldId, defaultRequired);
    return {
      required,
      'aria-required': required,
      'data-field-id': fieldId
    };
  }, [isRequired]);

  /**
   * Validate form data against required fields
   * @param {object} formData - Form data object
   * @param {array} fieldDefinitions - Array of field definitions with {id, label, defaultRequired}
   * @returns {object} { isValid: boolean, errors: array of error messages }
   */
  const validateForm = useCallback((formData, fieldDefinitions) => {
    const errors = [];
    
    fieldDefinitions.forEach(field => {
      const required = isRequired(field.id, field.defaultRequired || false);
      if (required) {
        const value = formData[field.id];
        const isEmpty = value === undefined || value === null || value === '' || 
                       (Array.isArray(value) && value.length === 0);
        
        if (isEmpty) {
          errors.push({
            field: field.id,
            message: `${field.label} es requerido`
          });
        }
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }, [isRequired]);

  /**
   * Get label with required indicator
   * @param {string} fieldId - ID of the field
   * @param {string} label - Base label text
   * @param {boolean} defaultRequired - Default required value
   * @returns {string} Label with * if required
   */
  const getLabel = useCallback((fieldId, label, defaultRequired = false) => {
    const required = isRequired(fieldId, defaultRequired);
    return required ? `${label} *` : label;
  }, [isRequired]);

  return {
    camposConfig,
    loading,
    error,
    isRequired,
    getFieldProps,
    validateForm,
    getLabel,
    refetch: fetchConfig
  };
}

/**
 * Higher-order component to wrap forms with campos obligatorios
 */
export function withCamposObligatorios(WrappedComponent, modulo, formulario) {
  return function EnhancedComponent(props) {
    const camposObligatorios = useCamposObligatorios(modulo, formulario, props.getAuthHeaders);
    
    return (
      <WrappedComponent
        {...props}
        camposObligatorios={camposObligatorios}
      />
    );
  };
}

export default useCamposObligatorios;
